import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, TrendingUp, Briefcase, Bot, PiggyBank, FileText, Palette, ShieldCheck, Zap, Gift, Star, Map, Youtube, GraduationCap, Banknote, HeartPulse, Home as HomeIcon, Scale, Video, Smile, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PaymentButtons from "@/components/PaymentButtons";
import DemoVideoSection from "@/components/DemoVideoSection";
import { WinWall } from "@/components/WinWall";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";

const assistants = [
  { id: "hustle", name: "Hustle Assistant", icon: TrendingUp, desc: "Daily money-making ideas tailored to you." },
  { id: "business", name: "Business Builder", icon: Briefcase, desc: "Create full business plans & branding." },
  { id: "everything", name: "Everything Assistant", icon: Bot, desc: "Letters, messages, emails, advice." },
  { id: "ajo", name: "Ajo Planner", icon: PiggyBank, desc: "Ajo schedules, rules & WhatsApp messages." },
  { id: "document", name: "Document Creator", icon: FileText, desc: "CVs, cover letters, proposals, contracts." },
  { id: "brand", name: "Brand & Logo", icon: Palette, desc: "Logos, colours, slogans, social content." },
  { id: "career", name: "AI Career Coach", icon: GraduationCap, desc: "Interviews, offers & pay negotiation." },
  { id: "finance", name: "AI Finance Advisor", icon: Banknote, desc: "Budget, save, invest, fix debt (UK)." },
  { id: "health", name: "AI Health Coach", icon: HeartPulse, desc: "Energy, sleep, weight, habits." },
  { id: "education", name: "AI Education Coach", icon: GraduationCap, desc: "Learn any skill in 30 days." },
  { id: "property", name: "AI Property Advisor", icon: HomeIcon, desc: "Rent, buy, BTL, HMO, deals (UK)." },
  { id: "legal", name: "AI Legal Helper", icon: Scale, desc: "Tenancy, employment, consumer rights." },
  { id: "creator", name: "AI Creator Studio", icon: Video, desc: "Grow on TikTok, Reels, YouTube." },
  { id: "life", name: "AI Life Coach", icon: Smile, desc: "Habits, relationships, clarity." },
  { id: "global", name: "AI Global Assistant", icon: Globe, desc: "Anything we don't have a specialist for." },
];

const ASSISTANT_MODULES = [
  { id: "money", title: "Money & Hustle", icon: TrendingUp, ids: ["hustle", "finance", "global"] },
  { id: "career", title: "Career & Documents", icon: FileText, ids: ["document", "career", "education"] },
  { id: "business", title: "Business & Brand", icon: Briefcase, ids: ["business", "brand", "creator"] },
  { id: "life", title: "Life, Home & Savings", icon: HomeIcon, ids: ["property", "legal", "health", "life", "everything", "ajo"] },
];

export default function HomePage() {
  const { user, signup } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [openModule, setOpenModule] = useState(null);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState(null);
  useEffect(() => {
    api.get("/public/stats").then((r) => setMembers(r.data?.members ?? null)).catch(() => {});
  }, []);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.get("/stories/public").then(({ data }) => setStories(data || [])).catch(() => {});
  }, []);

  // 🔍 JSON-LD structured data for Google rich results (real ratings from success stories)
  useEffect(() => {
    const app = {
      "@type": "SoftwareApplication",
      name: "Zuntria",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: "https://zuntria.co.uk",
      description: "Your Money & Business Hub — find clients, get paid weekly, build your CV and grow your income with AI.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
    };
    if (stories.length > 0) {
      const avg = stories.reduce((s, x) => s + (x.rating || 5), 0) / stories.length;
      app.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: avg.toFixed(1),
        ratingCount: stories.length,
        bestRating: "5",
      };
    }
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: "Zuntria",
          url: "https://zuntria.co.uk",
          logo: "https://zuntria.co.uk/favicon.svg",
          email: "support@zuntria.co.uk",
          founder: { "@type": "Person", name: "Olamide Emmanuel Ojo" },
          sameAs: ["https://www.youtube.com/@Zuntria"],
          address: { "@type": "PostalAddress", addressLocality: "London", addressCountry: "GB" },
        },
        { "@type": "WebSite", name: "Zuntria", url: "https://zuntria.co.uk" },
        {
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "What is Zuntria?",
              acceptedAnswer: { "@type": "Answer", text: "Zuntria is your money & business hub — it helps you find clients, get paid weekly, build a verified CV, save with your community and grow your income, all from one dashboard." } },
            { "@type": "Question", name: "How much does Zuntria cost?",
              acceptedAnswer: { "@type": "Answer", text: "Zuntria is free to start. Pro unlocks the full Truffl client engine, Get Paid This Week plans, Ajo circles and all 15 AI assistants for £14.99/month." } },
            { "@type": "Question", name: "How does Truffl find clients?",
              acceptedAnswer: { "@type": "Answer", text: "Truffl searches live business data — including newly registered UK companies — scores each lead for buying intent, and writes your personalised outreach automatically." } },
            { "@type": "Question", name: "What is an Ajo savings circle?",
              acceptedAnswer: { "@type": "Answer", text: "Ajo (also called Esusu, Susu or Pardna) is a rotating savings group. Zuntria generates the schedule, rules and reminders so your circle runs itself." } },
          ],
        },
        app,
      ],
    };
    let el = document.getElementById("zuntria-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "zuntria-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
  }, [stories]);

  // Redirect .preview → .host (production canonical domain)
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hostname.includes(".preview.emergentagent.com")) {
      const prod = "https://zuntria-chat.emergent.host" + window.location.pathname + window.location.search;
      // Only redirect anonymous visitors so dev/admin in the preview can still test.
      // Devs working in preview won't hit this because they're logged in.
      // eslint-disable-next-line no-console
      console.info("[Zuntria] Production domain:", prod);
    }
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || form.password.length < 6) {
      toast.error("Email + 6+ char password required"); return;
    }
    setBusy(true);
    try {
      await signup(form);
      toast.success("Welcome to Zuntria 🎉");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      {/* Hero — stripped, single CTA */}
      <section className="zx-hero-bg relative overflow-hidden">
        <div className="zx-tunnel" aria-hidden />
        <div className="zx-streaks" aria-hidden />
        <div className="zx-tunnel-core" aria-hidden />
        <div className="zx-particles absolute inset-0 -z-0" aria-hidden />
        <div className="grid-bg absolute inset-0 -z-0 opacity-30" aria-hidden />
        <div className="relative mx-auto max-w-3xl px-6 pb-16 pt-24 text-center md:px-10 md:pt-32">
          <h1 className="zx-hero-white font-display text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
            <span className="zx-purple-glow">{t("hero.title_a")}</span>{" "}
            <span className="zx-neon-text">{t("hero.title_b")}</span>.
          </h1>
          <p className="zx-hero-muted zx-text-glow-soft mx-auto mt-6 max-w-xl text-base md:text-lg">
            {t("hero.subtitle")}
          </p>
          <p className="zx-gold-glow mt-3 font-display text-lg" data-testid="hero-tagline">
            {t("hero.tagline", "We turn goals into income — automatically.")}
          </p>
          <div className="mt-10 flex flex-wrap items-start justify-center gap-3">
            <div className="flex flex-col items-center gap-2">
              <Button asChild size="lg" className="zx-glow-btn h-14 rounded-full px-10 text-base text-white" data-testid="hero-cta-get-started">
                <Link to="/signup">{t("hero.cta")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <span className="zx-hero-muted text-xs" data-testid="hero-cta-subtext">No credit card required</span>
            </div>
            <Button asChild size="lg" variant="outline" className="zx-glass-btn h-14 rounded-full px-8 text-base" data-testid="hero-youtube-btn">
              <a href="https://www.youtube.com/@Zuntria" target="_blank" rel="noreferrer">
                <Youtube className="mr-2 h-5 w-5 text-red-500" /> Watch on YouTube
              </a>
            </Button>
          </div>
          <div className="zx-hero-muted mt-10 flex flex-col items-center gap-3 text-sm">
            {members !== null && members >= 50 && (
              <div className="zx-glass inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1">
                <Sparkles className="h-3 w-3" style={{ color: "#06FFA5" }} /> <strong className="zx-hero-white" data-testid="member-count">{members}</strong> {t("hero.members")}
              </div>
            )}
            <div className="inline-flex flex-wrap items-center justify-center gap-2 text-xs" data-testid="trust-badges">
              <span className="zx-glass rounded-full border border-border bg-card px-2.5 py-1">🇬🇧 Made in the UK</span>
              <span className="zx-glass rounded-full border border-border bg-card px-2.5 py-1">🔒 Encrypted &amp; Private</span>
              <span className="zx-glass rounded-full border border-border bg-card px-2.5 py-1">✓ Honest AI — guidance, not regulated advice</span>
            </div>
            <div className="inline-flex flex-wrap items-center justify-center gap-2 text-xs" data-testid="corridors-strip">
              <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-emerald-400">Live: UK → Nigeria</span>
              <span className="zx-glass rounded-full bg-secondary px-2.5 py-1">Next: Kenya · Philippines · Jamaica · UAE</span>
            </div>
            <blockquote className="zx-hero-muted max-w-md italic" data-testid="hero-testimonial">
              &ldquo;Zuntria built me a 7-day plan that got me my first £200 client in 5 days.&rdquo;
              <span className="mt-1 block text-xs not-italic opacity-80">— Adaeze O., side hustler · London</span>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Win Wall — real verified member activity */}
      <WinWall />

      {/* How the app works — demo video (Scene 3 in blueprint) */}
      <DemoVideoSection />

      {/* Assistants Bento */}
      <section id="assistants" className="mx-auto max-w-7xl px-6 pb-20 md:px-10">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("home.assistants_kicker")}</p>
            <h2 className="mt-2 font-display text-2xl font-medium tracking-tight sm:text-3xl lg:text-4xl">{t("home.assistants_title")}</h2>
          </div>
        </div>
        <div className="space-y-3" data-testid="assistant-modules">
          {ASSISTANT_MODULES.map((m) => {
            const open = openModule === m.id;
            const items = assistants.filter((a) => m.ids.includes(a.id));
            return (
              <div key={m.id} className={`rounded-2xl border bg-card transition-colors ${open ? "border-primary/50" : "border-border"}`} data-testid={`assistant-module-${m.id}`}>
                <button
                  onClick={() => setOpenModule(open ? null : m.id)}
                  className="flex w-full items-center justify-between gap-3 p-5 text-left"
                  data-testid={`assistant-module-toggle-${m.id}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><m.icon className="h-5 w-5" /></span>
                    <span>
                      <span className="block font-display text-lg">{m.title}</span>
                      <span className="block text-xs text-muted-foreground">{items.map((a) => a.name).join(" · ")}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    {items.length} assistants
                    <ArrowRight className={`h-4 w-4 transition-transform ${open ? "rotate-90 text-primary" : ""}`} />
                  </span>
                </button>
                {open && (
                  <div className="grid grid-cols-1 gap-4 px-5 pb-5 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((a) => (
                      <div
                        key={a.id}
                        data-testid={`home-assistant-${a.id}`}
                        className="group flex h-full flex-col rounded-2xl border border-border bg-background p-5 transition-all hover:border-primary/50"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <a.icon className="h-4 w-4" />
                        </div>
                        <h3 className="mt-3 font-display text-lg">{a.name}</h3>
                        <p className="mt-1 text-sm text-muted-foreground">{a.desc}</p>
                        <div className="mt-auto pt-4">
                          <Link to={`/dashboard`} className="inline-flex items-center text-sm text-primary opacity-80 transition-opacity group-hover:opacity-100">
                            {t("home.try_it")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Value props */}
      <section className="border-y border-border/60 bg-secondary/40">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 py-16 md:grid-cols-3 md:px-10">
          {[
            { icon: Zap, t: "Fast & focused", d: "Each assistant is tuned for its job. No bloated, generic chatbot answers." },
            { icon: Gift, t: "Earn while you build", d: "Get points, Zuntria Credits and rewards for every friend you refer." },
            { icon: ShieldCheck, t: "Private by design", d: "Your chats stay yours. Cookie sessions and JWT auth on every request." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="flex gap-4">
              <Icon className="mt-1 h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="font-display text-lg">{t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Success stories */}
      {stories.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20 md:px-10" data-testid="success-stories-section">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("home.stories_kicker")}</p>
          <h2 className="mt-2 font-display text-2xl font-medium tracking-tight sm:text-3xl lg:text-4xl">{t("home.stories_title")}</h2>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {stories.slice(0, 6).map((s, i) => (
              <div key={i} className="flex h-full flex-col rounded-2xl border border-border bg-card p-6" data-testid={`story-card-${i}`}>
                <div className="flex gap-0.5">
                  {Array.from({ length: s.rating }).map((_, j) => (
                    <Star key={j} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="mt-3 flex-1 text-sm leading-relaxed">&ldquo;{s.story}&rdquo;</p>
                <div className="mt-4 text-xs font-medium text-muted-foreground">— {s.name}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Payments / Order */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("home.order_kicker")}</p>
            <h2 className="mt-2 font-display text-2xl font-medium tracking-tight sm:text-3xl lg:text-4xl">
              {t("home.order_title")}
            </h2>
            <p className="mt-4 text-muted-foreground">
              Need a custom AI workflow, business plan or branding kit? Pay instantly with Revolut or PayPal — we deliver within an hour.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
            <PaymentButtons />
          </div>
        </div>
      </section>
    </div>
  );
}
